import _ from 'underscore';

import {
  ImageExperienceImage, constants as C
} from '@bingads-webui-campaign-react/image-experience-model';

const AspectRatioTolerance = 0.01;

// Attention!!: DO NOT touch this algorithm unless sync with MT/DE.
// This logic is also implemented on DE side.
// originalBoxToCrop { SourceWidth, SourceHeight, SourceX, SourceY }
export const getCenterCroppedSettings = (originalBoxToCrop, aspectRatio, shouldIgnoreCropSetting, setCroppingType) => {
  const {
    SourceWidth: centerContainerWidth,
    SourceHeight: centerContainerHeight,
  } = originalBoxToCrop;
  const originalRatio = centerContainerWidth / centerContainerHeight;
  const cropSettings = _.defaults({}, originalBoxToCrop);

  if (shouldIgnoreCropSetting) {
    return cropSettings;
  }

  if (Math.abs(originalRatio - aspectRatio) > AspectRatioTolerance) {
    // If the target ratio is narrower than the original
    if (aspectRatio <= originalRatio) {
      // Use the same height, get the width from the original height
      const scaledSourceWidth = Math.round(cropSettings.SourceHeight * aspectRatio);

      // Since we use the full height, Y is still 0
      // Make sure we crop the middle section horionzontally
      const centerX =
        Math.round((cropSettings.SourceWidth - scaledSourceWidth) / 2) + cropSettings.SourceX;

      cropSettings.SourceWidth = scaledSourceWidth;
      cropSettings.SourceX = centerX;
    } else {
      // Use the same width, get the height from the original width
      const scaledSourceHeight = Math.round(cropSettings.SourceWidth / aspectRatio);

      // We use full width, X is 0.
      // Make sure we crop the middle section vertically
      const middleY = Math.round((
        cropSettings.SourceHeight - scaledSourceHeight) / 2) + cropSettings.SourceY;

      cropSettings.SourceHeight = scaledSourceHeight;
      cropSettings.SourceY = middleY;
    }
  }

  if (setCroppingType) {
    cropSettings.CroppingType = 'CenterCrop';
  }

  return cropSettings;
};

export const getAutoCroppedImagesForRatios = ({
  originalImageToCrop,
  aspectRatios,
  maxCropBox,
  imageOption = C.defaultImageOption,
  aspectRatioOption = C.defaultAspectRatioOption,
  isRecommendedImage = false,
  defaultRatioToInclude, // optional, if omit, compute a ratio from aspectRatios
  disableEdit = false,
  useOriginalImageSize = false,
  setCroppingType = false,
}) => {
  const width = useOriginalImageSize ? originalImageToCrop.OriginalWidth : originalImageToCrop.Width;
  const height = useOriginalImageSize ? originalImageToCrop.OriginalHeight : originalImageToCrop.Height;
  const src = useOriginalImageSize ? originalImageToCrop.OriginalUrl : originalImageToCrop.Src;
  const boxToCrop = maxCropBox || {
    SourceWidth: width,
    SourceHeight: height,
    SourceX: 0,
    SourceY: 0,
  };
  const defaultRatio = defaultRatioToInclude ||
    (imageOption === C.imageOptionKeys.Logo && aspectRatioOption ?
      _.result(aspectRatios, aspectRatioOption) :
      C.getDefaultRatio(boxToCrop.SourceWidth, boxToCrop.SourceHeight, aspectRatios));

  const imagesToReturn = _.map(
    aspectRatios,
    (cropInfo) => {
      const croppedImageData = {
        id: originalImageToCrop.Id,
        idString: originalImageToCrop.IdString,
        src,
        width,
        height,
        text: originalImageToCrop.Text,
        source: originalImageToCrop.source,
        originalSourceType: originalImageToCrop.originalSourceType,
      };

      // By default, the whole image is the crop setting
      const cropSettings = getCenterCroppedSettings(boxToCrop, cropInfo.aspectRatio, cropInfo.shouldIgnoreCropSetting, setCroppingType);

      croppedImageData.cropSettings = cropSettings;
      croppedImageData.type = cropInfo.type;
      croppedImageData.associationType = cropInfo.associationType;
      croppedImageData.isLogo = cropInfo.isLogo;
      croppedImageData.isPortrait = cropInfo.isPortrait;
      croppedImageData.isIncluded = cropInfo.aspectRatio === _.result(defaultRatio, 'aspectRatio');
      croppedImageData.isRecommendedImage = isRecommendedImage;
      croppedImageData.disableEdit = disableEdit;

      return new ImageExperienceImage(croppedImageData);
    }
  );

  return imagesToReturn;
};

export const processCroppedImages = ({
  imageOption,
  croppedImages,
  aspectRatios,
  isMultimediaAdinMSANEnabled,
}) => {
  croppedImages.forEach((image) => {
    if (imageOption !== C.imageOptionKeys.Logo) {
      if (isMultimediaAdinMSANEnabled) {
        image.setIncluded(true);
        image.setAutoCropping(true);
      }

      const aspectRatio = _.findWhere(aspectRatios, { type: image.type });
      if (image.width < aspectRatio.minSize[0] || image.height < aspectRatio.minSize[1]) {
        image.setIncluded(false);
      }
    }
  });
};

/**
 * Return the image objects
 *
 * @param {Object} originalImageToCrop - ImageExperienceImage Object
 * @param {boolean} isMultimediaAd - If it is responsive for Multimedia Ads
 * @param {object} maxCropBox - Max crop box
 * @returns {Array} - Array of image objects
 */
export const getAutoCroppedImages = ({
  originalImageToCrop,
  isMultimediaAd = false,
  isMultimediaAdinMSANEnabled,
  maxCropBox,
  imageOption = C.defaultImageOption,
  aspectRatioOption = C.defaultAspectRatioOption,
  isRecommendedImage = false,
  isCombinedMsanAndMmaAspectRatios,
  disableEdit = false,
  isMMAV2Enabled = false,
  isDisplayAds = false,
  isPortraitImage = false,
  isBoost200x100ImageEnabled = false,
  isBoostNewAspectRatioSupportEnabled = false,
  isStoreAppProng = false,
  isStoreAppSpotLight = false,
  isStoreAppTransparentLogo = false,
  isLinkedInAd = false,
  isUnifiedAppCampaignWindowsStore = false,
  setCroppingType = false,
  isPMaxAspectRatiosV2Enabled = false,
}) => {
  const aspectRatios = C.getAspectRatios({
    isMultimediaAd,
    isMultimediaAdinMSANEnabled,
    isCombinedMsanAndMmaAspectRatios,
    imageOption,
    isMMAV2Enabled,
    isDisplayAds,
    isPortraitImage,
    isBoost200x100ImageEnabled,
    isBoostNewAspectRatioSupportEnabled,
    isStoreAppProng,
    isStoreAppSpotLight,
    isStoreAppTransparentLogo,
    isLinkedInAd,
    isUnifiedAppCampaignWindowsStore,
    isPMaxAspectRatiosV2Enabled,
  });

  // wrap all logics into method getAutoCroppedImagesForRatios
  const autoCroppingImages = getAutoCroppedImagesForRatios({
    originalImageToCrop,
    aspectRatios,
    maxCropBox,
    imageOption,
    aspectRatioOption,
    isRecommendedImage,
    disableEdit,
    useOriginalImageSize: isDisplayAds,
    setCroppingType,
  });

  processCroppedImages({
    imageOption,
    croppedImages: autoCroppingImages,
    aspectRatios,
    isMultimediaAdinMSANEnabled,
  });

  return autoCroppingImages;
};
